import React, { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import { registerUser } from "../../../redux/userRelated/userHandle";
import Popup from "../../../components/Popup";
import { FEEDBACK, SUCCESS_NAVIGATE_DELAY_MS, formatAdminError, ERROR_HINTS } from "../../../utils/feedbackMessages";
import { underControl } from "../../../redux/userRelated/userSlice";
import { getAllSclasses, getSubjectList, updateSclass } from "../../../redux/sclassRelated/sclassHandle";
import { getSecondaryFormLevel } from "../../../utils/classScopeMatch";
import { FORM3_MIN_SUBJECTS, isSubjectChoiceFormLevel } from "../../../utils/form3SubjectSelection";
import Form3SubjectPicker from "../../../components/class/Form3SubjectPicker";
import HostelRoomPickerFields from "../../../components/student/HostelRoomPickerFields";
import { fetchHostelsForEnrollment, findHostelIdForRoom } from "../../../utils/hostelEnrollmentOptions";
import { getAllTeachers } from "../../../redux/teacherRelated/teacherHandle";
import { useHomeroomTeacherTerm } from "../../../hooks/useHomeroomTeacherTerm";
import { getHomeroomStaffRoleValue, isHomeroomTeacherRole, normalizeStaffRole } from "../../../utils/staffRoles";
import {
    alpha,
    Alert,
    Avatar,
    Box,
    Button,
    Container,
    Divider,
    FormControl,
    Grid,
    InputLabel,
    MenuItem,
    Paper,
    Select,
    Stack,
    TextField,
    Typography,
    CircularProgress,
} from "@mui/material";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import PhotoCameraIcon from "@mui/icons-material/PhotoCamera";
import { resolveUploadedMediaUrl } from "../../../config/api";
import {
    defaultResidenceType,
    normalizeBoardingMode,
    requiresHostelAssignment,
    showResidencePicker,
    schoolOffersBoarding,
} from "../../../utils/boardingUtils";
import { isAtStudentLimit } from "../../../config/subscriptionPlans";
import { fetchNextStudentId } from "../../../utils/studentIdUtils";
import { studentMatchesHostel } from "../../../utils/hostelGenderUtils";
import { GUARDIAN_RELATIONS } from "../../../components/student/studentEnrollmentConstants";

const GENDERS = ["Male", "Female"];
const ADMISSION_STATUSES = ["Active", "Transferred", "Dropped", "Graduated"];
const YEAR_OF_STUDY_OPTIONS = [1, 2, 3, 4, 5];
const SPONSOR_TYPES = ["Self-sponsored", "Government bursary", "Scholarship", "Student loan", "Other"];
const MALAWI_DISTRICTS = [
    "Balaka",
    "Blantyre",
    "Chikwawa",
    "Chiradzulu",
    "Chitipa",
    "Dedza",
    "Dowa",
    "Karonga",
    "Kasungu",
    "Likoma",
    "Lilongwe",
    "Machinga",
    "Mangochi",
    "Mchinji",
    "Mulanje",
    "Mwanza",
    "Mzimba",
    "Neno",
    "Nkhata Bay",
    "Nkhotakota",
    "Nsanje",
    "Ntcheu",
    "Ntchisi",
    "Phalombe",
    "Rumphi",
    "Salima",
    "Thyolo",
    "Zomba",
];

const getExamRegistrationField = (selectedClass) => {
    if (!selectedClass?.isExamClass) return null;
    const code = String(selectedClass.levelCode || "").trim();
    if (code === "Std8") {
        return {
            key: "pslceRegNumber",
            label: "PSLCE Registration Number",
        };
    }
    if (code === "Form2") {
        return {
            key: "jceRegNumber",
            label: "JCE Registration Number",
        };
    }
    if (code === "Form4") {
        return {
            key: "msceRegNumber",
            label: "MSCE Registration Number",
        };
    }
    return null;
};

const AddStudent = ({ situation }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const params = useParams();

    const userState = useSelector((state) => state.user);
    const { status, currentUser, response, error, tempDetails } = userState;
    const { sclassesList, subjectsList, loading: subjectsLoading } = useSelector((state) => state.sclass);
    const { teachersList } = useSelector((state) => state.teacher);
    const homeroomTerm = useHomeroomTeacherTerm();
    const homeroomRoleValue = getHomeroomStaffRoleValue(currentUser?.schoolType);

    const [name, setName] = useState("");
    const [studentIdPreview, setStudentIdPreview] = useState("");
    const [password, setPassword] = useState("");
    const [sclassName, setSclassName] = useState("");
    const [classTeacherId, setClassTeacherId] = useState("");
    const [savingHomeroomTeacher, setSavingHomeroomTeacher] = useState(false);

    const [photoUrl, setPhotoUrl] = useState("");
    const [photoFile, setPhotoFile] = useState(null);
    const [previewUrl, setPreviewUrl] = useState(null);
    const fileInputRef = useRef(null);

    const [gender, setGender] = useState("");
    const [dateOfBirth, setDateOfBirth] = useState("");
    const [nationality, setNationality] = useState("Malawian");
    const [district, setDistrict] = useState("");
    const [admissionDate, setAdmissionDate] = useState("");
    const [admissionStatus, setAdmissionStatus] = useState("Active");

    const [guardianName, setGuardianName] = useState("");
    const [guardianPhone, setGuardianPhone] = useState("");
    const [guardianRelation, setGuardianRelation] = useState("");
    const [secondaryGuardianPhone, setSecondaryGuardianPhone] = useState("");
    const [studentPhone, setStudentPhone] = useState("");
    const [guardianEmail, setGuardianEmail] = useState("");

    const [pslceRegNumber, setPslceRegNumber] = useState("");
    const [jceRegNumber, setJceRegNumber] = useState("");
    const [msceRegNumber, setMsceRegNumber] = useState("");

    const [studentRegNumber, setStudentRegNumber] = useState("");
    const [programme, setProgramme] = useState("");
    const [faculty, setFaculty] = useState("");
    const [department, setDepartment] = useState("");
    const [yearOfStudy, setYearOfStudy] = useState(1);
    const [gpa] = useState("");
    const [sponsorType, setSponsorType] = useState("");
    const [nextOfKinName, setNextOfKinName] = useState("");
    const [nextOfKinPhone, setNextOfKinPhone] = useState("");
    const [nextOfKinRelation, setNextOfKinRelation] = useState("");

    const boardingMode = normalizeBoardingMode(currentUser?.boardingMode, currentUser?.hasBoarding);
    const offersBoarding = schoolOffersBoarding(boardingMode);
    const showResidenceSection = offersBoarding;
    const canPickResidence = showResidencePicker(boardingMode);
    const [residenceType, setResidenceType] = useState(() => defaultResidenceType(boardingMode));
    const [hostelId, setHostelId] = useState("");
    const [hostelRoomId, setHostelRoomId] = useState("");
    const [bedLabel, setBedLabel] = useState("");
    const [hostels, setHostels] = useState([]);

    const adminID = currentUser._id;
    const role = "Student";

    const [showPopup, setShowPopup] = useState(false);
    const [popupSeverity, setPopupSeverity] = useState(undefined);
    const [message, setMessage] = useState("");
    const [loader, setLoader] = useState(false);
    const [form3SubjectIds, setForm3SubjectIds] = useState(() => new Set());

    useEffect(() => {
        if (situation === "Class") {
            setSclassName(params.id);
        }
    }, [params.id, situation]);

    useEffect(() => {
        dispatch(getAllSclasses(adminID, "Sclass"));
        dispatch(getAllTeachers(adminID));
    }, [adminID, dispatch]);

    useEffect(() => {
        if (!offersBoarding || !adminID) {
            setHostels([]);
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const rows = await fetchHostelsForEnrollment(adminID);
                if (!cancelled) setHostels(rows);
            } catch {
                if (!cancelled) setHostels([]);
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [adminID, offersBoarding]);

    useEffect(() => {
        return () => {
            if (previewUrl) URL.revokeObjectURL(previewUrl);
        };
    }, [previewUrl]);

    const selectedClass = useMemo(() => {
        return (
            sclassesList.find((classItem) => String(classItem._id || classItem.id) === String(sclassName)) ||
            null
        );
    }, [sclassName, sclassesList]);

    useEffect(() => {
        const tid = selectedClass?.classTeacher?._id ?? selectedClass?.classTeacher?.id ?? "";
        setClassTeacherId(tid ? String(tid) : "");
    }, [selectedClass?.classTeacher?._id, selectedClass?.classTeacher?.id, sclassName]);

    const selectedEducationLevel = selectedClass?.educationLevel || "";
    const isUniversity = selectedEducationLevel === "University";
    const showGuardianSection = Boolean(selectedEducationLevel) && !isUniversity;
    const examRegistrationField = useMemo(() => getExamRegistrationField(selectedClass), [selectedClass]);
    const subjectChoiceFormLevel = getSecondaryFormLevel(selectedClass);
    const isSubjectChoiceClass = isSubjectChoiceFormLevel(subjectChoiceFormLevel);
    const form3Subjects = useMemo(
        () => (Array.isArray(subjectsList) ? subjectsList : []),
        [subjectsList],
    );

    useEffect(() => {
        if (!isSubjectChoiceClass || !sclassName) {
            setForm3SubjectIds(new Set());
            return;
        }
        dispatch(getSubjectList(sclassName, "ClassSubjects"));
        setForm3SubjectIds(new Set());
    }, [dispatch, isSubjectChoiceClass, sclassName]);

    const toggleForm3Subject = (subjectId) => {
        setForm3SubjectIds((prev) => {
            const next = new Set(prev);
            if (next.has(subjectId)) next.delete(subjectId);
            else next.add(subjectId);
            return next;
        });
    };

    const homeroomTeacherCandidates = useMemo(() => {
        if (!Array.isArray(teachersList)) return [];
        return teachersList.filter((t) => {
            const r = normalizeStaffRole(t.role);
            return isHomeroomTeacherRole(r) || r === "Teacher" || r === "Head of Department";
        });
    }, [teachersList]);

    const saveHomeroomTeacherOnClass = async () => {
        if (!sclassName) return;
        setSavingHomeroomTeacher(true);
        await dispatch(updateSclass(sclassName, { classTeacherId: classTeacherId || "" }));
        dispatch(getAllSclasses(adminID, "Sclass"));
        setSavingHomeroomTeacher(false);
    };

    const handlePhotoPick = (event) => {
        const f = event.target.files?.[0];
        if (previewUrl) {
            URL.revokeObjectURL(previewUrl);
            setPreviewUrl(null);
        }
        if (!f) {
            setPhotoFile(null);
            return;
        }
        setPhotoUrl("");
        setPhotoFile(f);
        setPreviewUrl(URL.createObjectURL(f));
    };

    const clearPhotoPick = () => {
        if (previewUrl) URL.revokeObjectURL(previewUrl);
        setPreviewUrl(null);
        setPhotoFile(null);
        setPhotoUrl("");
        if (fileInputRef.current) fileInputRef.current.value = "";
    };

    const changeHandler = (event) => {
        const value = event.target.value;
        setSclassName(value || "");
    };

    useEffect(() => {
        if (!sclassName) {
            setStudentIdPreview("");
            return;
        }
        let cancelled = false;
        (async () => {
            try {
                const data = await fetchNextStudentId(sclassName);
                if (!cancelled && data?.nextStudentId != null) {
                    setStudentIdPreview(String(data.nextStudentId));
                }
            } catch {
                if (!cancelled) setStudentIdPreview("");
            }
        })();
        return () => {
            cancelled = true;
        };
    }, [sclassName]);

    const studentLimitReached = isAtStudentLimit(currentUser);
    const studentLimit = currentUser?.studentLimit;
    const enrolledCount = currentUser?.schoolStudentCount ?? 0;

    const submitHandler = (event) => {
        event.preventDefault();
        if (studentLimitReached) {
            setMessage(
                studentLimit != null
                    ? `Your plan allows up to ${studentLimit} students and you already have ${enrolledCount}. Upgrade your subscription before adding more.`
                    : "You cannot add more students on your current plan.",
            );
            setPopupSeverity("warning");
            setShowPopup(true);
            return;
        }
        if (!sclassName) {
            setMessage("Please select a class.");
            setShowPopup(true);
            return;
        }
        if (isUniversity && !studentRegNumber.trim()) {
            setMessage("Student registration number is required for university students.");
            setShowPopup(true);
            return;
        }
        if (requiresHostelAssignment(boardingMode, currentUser?.hasBoarding, residenceType) && !hostelRoomId) {
            setMessage("Boarding students must be assigned a hostel room.");
            setShowPopup(true);
            return;
        }
        if (residenceType === "Boarding" && hostelRoomId) {
            const hid = hostelId || findHostelIdForRoom(hostels, hostelRoomId);
            const hostel = hostels.find((h) => h.id === hid);
            const match = studentMatchesHostel(gender, hostel?.gender);
            if (!match.ok) {
                setMessage(match.message);
                setShowPopup(true);
                return;
            }
        }
        if (isSubjectChoiceClass) {
            if (form3Subjects.length < FORM3_MIN_SUBJECTS) {
                setMessage(
                    `This ${subjectChoiceFormLevel} class needs at least ${FORM3_MIN_SUBJECTS} subjects before you can enroll a pupil.`,
                );
                setPopupSeverity("warning");
                setShowPopup(true);
                return;
            }
            if (form3SubjectIds.size < FORM3_MIN_SUBJECTS) {
                setMessage(`Select at least ${FORM3_MIN_SUBJECTS} subjects for this ${subjectChoiceFormLevel} pupil.`);
                setPopupSeverity("warning");
                setShowPopup(true);
                return;
            }
        }

        const rollNum = Number(studentIdPreview);
        if (!Number.isFinite(rollNum) || rollNum < 1) {
            setMessage("Student ID is not ready yet — wait a moment after selecting the class, then try again.");
            setShowPopup(true);
            return;
        }

        setLoader(true);

        const fields = {
            name,
            rollNum,
            password,
            sclassName,
            adminID,
            role,
            photoUrl: photoFile ? "" : photoUrl.trim(),
            gender: gender || undefined,
            dateOfBirth: dateOfBirth || undefined,
            nationality: nationality.trim() || "Malawian",
            district: district || undefined,
            admissionDate: admissionDate || undefined,
            admissionStatus,
            educationLevel: selectedEducationLevel || undefined,
            guardianName: showGuardianSection ? guardianName.trim() : undefined,
            guardianPhone: showGuardianSection ? guardianPhone.trim() : undefined,
            guardianRelation: showGuardianSection ? guardianRelation.trim() : undefined,
            secondaryGuardianPhone: showGuardianSection ? secondaryGuardianPhone.trim() : undefined,
            studentPhone: showGuardianSection ? studentPhone.trim() : undefined,
            guardianEmail: showGuardianSection ? guardianEmail.trim() : undefined,
            pslceRegNumber: examRegistrationField?.key === "pslceRegNumber" ? pslceRegNumber.trim() : undefined,
            jceRegNumber: examRegistrationField?.key === "jceRegNumber" ? jceRegNumber.trim() : undefined,
            msceRegNumber: examRegistrationField?.key === "msceRegNumber" ? msceRegNumber.trim() : undefined,
            studentRegNumber: isUniversity ? studentRegNumber.trim() : undefined,
            programme: isUniversity ? programme.trim() : undefined,
            faculty: isUniversity ? faculty.trim() : undefined,
            department: isUniversity ? department.trim() : undefined,
            yearOfStudy: isUniversity ? yearOfStudy : undefined,
            gpa: isUniversity && gpa !== "" ? gpa : undefined,
            sponsorType: isUniversity ? sponsorType : undefined,
            nextOfKinName: isUniversity ? nextOfKinName.trim() : undefined,
            nextOfKinPhone: isUniversity ? nextOfKinPhone.trim() : undefined,
            nextOfKinRelation: isUniversity ? nextOfKinRelation.trim() : undefined,
            residenceType: showResidenceSection ? residenceType : undefined,
            hostelRoomId: residenceType === "Boarding" && hostelRoomId ? hostelRoomId : undefined,
            bedLabel: residenceType === "Boarding" ? bedLabel.trim() : undefined,
            subjectIds: isSubjectChoiceClass ? [...form3SubjectIds] : undefined,
        };

        dispatch(registerUser(fields, role, photoFile || undefined));
    };

    useEffect(() => {
        if (status === "added") {
            const notice = tempDetails?.feeAssignmentNotice;
            dispatch(underControl());
            setLoader(false);
            setMessage(
                notice
                    ? `${FEEDBACK.studentAdded}\n\n${notice}`
                    : FEEDBACK.studentAdded
            );
            setPopupSeverity("success");
            setShowPopup(true);
            const timer = setTimeout(
                () => navigate(-1),
                notice ? 4500 : SUCCESS_NAVIGATE_DELAY_MS
            );
            return () => clearTimeout(timer);
        } else if (status === "failed") {
            setMessage(formatAdminError(response));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        } else if (status === "error") {
            setMessage(formatAdminError(error, ERROR_HINTS.network));
            setPopupSeverity("error");
            setShowPopup(true);
            setLoader(false);
        }
    }, [status, navigate, error, response, dispatch, tempDetails]);

    return (
        <Box
            sx={{
                minHeight: "100%",
                pb: 4,
                background: (t) =>
                    t.palette.mode === "light"
                        ? `linear-gradient(180deg, ${alpha(t.palette.primary.main, 0.05)} 0%, ${t.palette.grey[50]} 24%)`
                        : alpha(t.palette.common.black, 0.12),
            }}
        >
            <Container maxWidth="md" sx={{ py: { xs: 2, sm: 3 } }}>
                <Paper
                    elevation={0}
                    sx={{
                        borderRadius: 3,
                        overflow: "hidden",
                        border: (t) => `1px solid ${alpha(t.palette.divider, 0.9)}`,
                        boxShadow: (t) => `0 8px 32px ${alpha(t.palette.common.black, 0.06)}`,
                    }}
                >
                    <Box
                        sx={{
                            px: { xs: 2, sm: 3 },
                            py: 2.5,
                            background: (t) =>
                                `linear-gradient(115deg, ${t.palette.primary.dark} 0%, ${t.palette.primary.main} 50%, ${alpha(
                                    t.palette.primary.light,
                                    0.92
                                )} 100%)`,
                            color: "common.white",
                        }}
                    >
                        <Stack direction="row" alignItems="center" spacing={1.5}>
                            <PersonAddAlt1Icon sx={{ fontSize: 32, opacity: 0.95 }} />
                            <Box>
                                <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 1 }}>
                                    Enrolment
                                </Typography>
                                <Typography variant="h5" fontWeight={800}>
                                    Add student
                                </Typography>
                                <Typography variant="body2" sx={{ opacity: 0.9, mt: 0.25, maxWidth: 480 }}>
                                    Account, class placement, admission profile, and Malawi-specific registration details in one form.
                                </Typography>
                            </Box>
                        </Stack>
                    </Box>

                    {studentLimitReached && (
                        <Alert severity="warning" sx={{ mx: { xs: 2, sm: 3 }, mt: 2 }}>
                            Student limit reached ({enrolledCount} of {studentLimit} on your plan, including pending
                            form-teacher enrollments). Upgrade your plan to enrol more pupils, or remove students from
                            the register first.
                        </Alert>
                    )}
                    {!studentLimitReached ? (
                        <Alert severity="info" sx={{ mx: { xs: 2, sm: 3 }, mt: 2 }}>
                            Director enrollments are <strong>verified immediately</strong> and receive fees. Staff with the{" "}
                            <strong>{homeroomRoleValue}</strong> role can submit pupils from their class for approval under{" "}
                            <strong>Verify enrollments</strong> (plan slots include pending pupils).
                        </Alert>
                    ) : null}

                    <Box component="form" onSubmit={submitHandler} sx={{ p: { xs: 2, sm: 3 } }}>
                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                            Identity & access
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    required
                                    label="Full name"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    autoComplete="name"
                                    placeholder="Student legal name"
                                />
                            </Grid>
                            {situation === "Student" && (
                                <Grid item xs={12}>
                                    <FormControl fullWidth required>
                                        <InputLabel id="add-student-class-label">Class</InputLabel>
                                        <Select
                                            labelId="add-student-class-label"
                                            label="Class"
                                            value={sclassName}
                                            onChange={changeHandler}
                                        >
                                            <MenuItem value="">
                                                <em>Select class</em>
                                            </MenuItem>
                                            {sclassesList.map((classItem) => (
                                                <MenuItem key={classItem._id || classItem.id} value={classItem._id || classItem.id}>
                                                    {classItem.sclassName}
                                                </MenuItem>
                                            ))}
                                        </Select>
                                    </FormControl>
                                </Grid>
                            )}
                            {isSubjectChoiceClass && sclassName ? (
                                <Grid item xs={12}>
                                    <Form3SubjectPicker
                                        formLevel={subjectChoiceFormLevel}
                                        subjects={form3Subjects}
                                        selectedIds={[...form3SubjectIds]}
                                        onToggle={toggleForm3Subject}
                                        loading={subjectsLoading}
                                    />
                                </Grid>
                            ) : null}
                            {situation === "Student" && sclassName ? (
                                <Grid item xs={12}>
                                    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: "action.hover" }}>
                                        <Typography variant="subtitle2" fontWeight={700} gutterBottom>
                                            {homeroomTerm.assignOnClass} for this class
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                                            Optional: link a staff member as {homeroomTerm.theRole}. They must have the{" "}
                                            <strong>{homeroomRoleValue}</strong> role (set under Staff → Assign roles) to
                                            enroll pupils from their portal. Pupils they add stay pending until you verify.
                                        </Typography>
                                        <Stack direction={{ xs: "column", sm: "row" }} spacing={1.5} alignItems="stretch">
                                            <FormControl fullWidth size="small">
                                                <InputLabel id="add-student-homeroom-label">
                                                    {homeroomTerm.assignOnClass}
                                                </InputLabel>
                                                <Select
                                                    labelId="add-student-homeroom-label"
                                                    label={homeroomTerm.assignOnClass}
                                                    value={classTeacherId}
                                                    onChange={(e) => setClassTeacherId(e.target.value)}
                                                >
                                                    <MenuItem value="">
                                                        <em>None</em>
                                                    </MenuItem>
                                                    {homeroomTeacherCandidates.map((t) => (
                                                        <MenuItem key={t._id} value={t._id}>
                                                            {t.name}
                                                            {!isHomeroomTeacherRole(t.role)
                                                                ? ` (${normalizeStaffRole(t.role)} — set role to ${homeroomRoleValue})`
                                                                : ""}
                                                        </MenuItem>
                                                    ))}
                                                </Select>
                                            </FormControl>
                                            <Button
                                                variant="outlined"
                                                onClick={saveHomeroomTeacherOnClass}
                                                disabled={savingHomeroomTeacher}
                                                sx={{ whiteSpace: "nowrap", alignSelf: { sm: "center" } }}
                                            >
                                                {savingHomeroomTeacher ? "Saving…" : "Save on class"}
                                            </Button>
                                        </Stack>
                                        {selectedClass?.classTeacher?.name ? (
                                            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                                Current: <strong>{selectedClass.classTeacher.name}</strong>
                                            </Typography>
                                        ) : null}
                                    </Paper>
                                </Grid>
                            ) : null}
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Student ID"
                                    value={studentIdPreview || "—"}
                                    InputProps={{ readOnly: true }}
                                    helperText={
                                        sclassName
                                            ? "Assigned automatically — next number in this class"
                                            : "Select a class first"
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    required
                                    type="password"
                                    label="Password"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    autoComplete="new-password"
                                />
                            </Grid>
                        </Grid>

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                            Profile
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12}>
                                <input
                                    ref={fileInputRef}
                                    type="file"
                                    accept="image/jpeg,image/png,image/gif,image/webp,image/*"
                                    hidden
                                    onChange={handlePhotoPick}
                                />
                                <Stack direction={{ xs: "column", sm: "row" }} spacing={2} alignItems={{ sm: "center" }}>
                                    <Avatar
                                        src={previewUrl || resolveUploadedMediaUrl(photoUrl) || undefined}
                                        alt="Preview"
                                        sx={{ width: 96, height: 96, fontSize: "2rem", fontWeight: 700 }}
                                    >
                                        {(name || "?").charAt(0).toUpperCase()}
                                    </Avatar>
                                    <Box sx={{ flex: 1 }}>
                                        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                                            <Button
                                                type="button"
                                                variant="outlined"
                                                startIcon={<PhotoCameraIcon />}
                                                onClick={() => fileInputRef.current?.click()}
                                                sx={{ borderRadius: 2 }}
                                            >
                                                Choose or take photo
                                            </Button>
                                            {(photoFile || previewUrl) && (
                                                <Button type="button" variant="text" color="error" onClick={clearPhotoPick} sx={{ borderRadius: 2 }}>
                                                    Remove photo
                                                </Button>
                                            )}
                                        </Stack>
                                        <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: "block" }}>
                                            JPEG, PNG, GIF, or WebP — max 5 MB. On a phone, your browser will offer camera or gallery.
                                        </Typography>
                                    </Box>
                                </Stack>
                            </Grid>
                            <Grid item xs={12}>
                                <TextField
                                    fullWidth
                                    label="Photo URL (optional)"
                                    value={photoUrl}
                                    onChange={(e) => setPhotoUrl(e.target.value)}
                                    placeholder="https://…"
                                    disabled={Boolean(photoFile)}
                                    helperText={
                                        photoFile
                                            ? "Remove the uploaded photo to use a link instead."
                                            : "Alternatively paste a hosted image URL if you are not uploading a file."
                                    }
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="gender-label">Gender</InputLabel>
                                    <Select
                                        labelId="gender-label"
                                        label="Gender"
                                        value={gender}
                                        onChange={(e) => setGender(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>Not specified</em>
                                        </MenuItem>
                                        {GENDERS.map((g) => (
                                            <MenuItem key={g} value={g}>
                                                {g}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Date of Birth"
                                    type="date"
                                    value={dateOfBirth}
                                    onChange={(e) => setDateOfBirth(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Nationality"
                                    value={nationality}
                                    onChange={(e) => setNationality(e.target.value)}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="district-label">District</InputLabel>
                                    <Select
                                        labelId="district-label"
                                        label="District"
                                        value={district}
                                        onChange={(e) => setDistrict(e.target.value)}
                                    >
                                        <MenuItem value="">
                                            <em>Not specified</em>
                                        </MenuItem>
                                        {MALAWI_DISTRICTS.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                        </Grid>

                        {showGuardianSection && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                                    Parent / Guardian
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Guardian Name"
                                            value={guardianName}
                                            onChange={(e) => setGuardianName(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="guardian-relation-label">Relationship to Student</InputLabel>
                                            <Select
                                                labelId="guardian-relation-label"
                                                label="Relationship to Student"
                                                value={guardianRelation}
                                                onChange={(e) => setGuardianRelation(e.target.value)}
                                            >
                                                <MenuItem value="">
                                                    <em>Not specified</em>
                                                </MenuItem>
                                                {GUARDIAN_RELATIONS.map((rel) => (
                                                    <MenuItem key={rel} value={rel}>
                                                        {rel}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Guardian Phone"
                                            value={guardianPhone}
                                            onChange={(e) => setGuardianPhone(e.target.value)}
                                            type="tel"
                                            helperText="Primary SMS contact for attendance, fees, results, and notices"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Secondary Guardian Phone"
                                            value={secondaryGuardianPhone}
                                            onChange={(e) => setSecondaryGuardianPhone(e.target.value)}
                                            type="tel"
                                            helperText="Optional — also receives school SMS alerts"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Student Phone"
                                            value={studentPhone}
                                            onChange={(e) => setStudentPhone(e.target.value)}
                                            type="tel"
                                            helperText="Optional — SMS when also-notify-student is enabled"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Guardian Email"
                                            type="email"
                                            value={guardianEmail}
                                            onChange={(e) => setGuardianEmail(e.target.value)}
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {showGuardianSection && examRegistrationField && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                                    Exam Registration
                                </Typography>
                                <Grid container spacing={2}>
                                    {examRegistrationField.key === "pslceRegNumber" && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="PSLCE Registration Number"
                                                value={pslceRegNumber}
                                                onChange={(e) => setPslceRegNumber(e.target.value)}
                                            />
                                        </Grid>
                                    )}
                                    {examRegistrationField.key === "jceRegNumber" && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="JCE Registration Number"
                                                value={jceRegNumber}
                                                onChange={(e) => setJceRegNumber(e.target.value)}
                                            />
                                        </Grid>
                                    )}
                                    {examRegistrationField.key === "msceRegNumber" && (
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="MSCE Registration Number"
                                                value={msceRegNumber}
                                                onChange={(e) => setMsceRegNumber(e.target.value)}
                                            />
                                        </Grid>
                                    )}
                                </Grid>
                            </>
                        )}

                        {isUniversity && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                                    University Registration
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            required
                                            label="Student Registration Number"
                                            value={studentRegNumber}
                                            onChange={(e) => setStudentRegNumber(e.target.value)}
                                            helperText="e.g. UNIMA/CS/2025/001"
                                        />
                                    </Grid>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Programme"
                                            value={programme}
                                            onChange={(e) => setProgramme(e.target.value)}
                                            placeholder="e.g. BSc Computer Science"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Faculty"
                                            value={faculty}
                                            onChange={(e) => setFaculty(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Department"
                                            value={department}
                                            onChange={(e) => setDepartment(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="year-of-study-label">Year of Study</InputLabel>
                                            <Select
                                                labelId="year-of-study-label"
                                                label="Year of Study"
                                                value={yearOfStudy}
                                                onChange={(e) => setYearOfStudy(e.target.value)}
                                            >
                                                {YEAR_OF_STUDY_OPTIONS.map((year) => (
                                                    <MenuItem key={year} value={year}>
                                                        {year}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <FormControl fullWidth>
                                            <InputLabel id="sponsor-type-label">Sponsor Type</InputLabel>
                                            <Select
                                                labelId="sponsor-type-label"
                                                label="Sponsor Type"
                                                value={sponsorType}
                                                onChange={(e) => setSponsorType(e.target.value)}
                                            >
                                                {SPONSOR_TYPES.map((item) => (
                                                    <MenuItem key={item} value={item}>
                                                        {item}
                                                    </MenuItem>
                                                ))}
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                </Grid>

                                <Divider sx={{ my: 3 }} />
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                                    Emergency Contact
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid item xs={12}>
                                        <TextField
                                            fullWidth
                                            label="Next of Kin Name"
                                            value={nextOfKinName}
                                            onChange={(e) => setNextOfKinName(e.target.value)}
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Next of Kin Phone"
                                            value={nextOfKinPhone}
                                            onChange={(e) => setNextOfKinPhone(e.target.value)}
                                            type="tel"
                                        />
                                    </Grid>
                                    <Grid item xs={12} sm={6}>
                                        <TextField
                                            fullWidth
                                            label="Relationship"
                                            value={nextOfKinRelation}
                                            onChange={(e) => setNextOfKinRelation(e.target.value)}
                                            placeholder="e.g. Mother, Father, Sibling"
                                        />
                                    </Grid>
                                </Grid>
                            </>
                        )}

                        {showResidenceSection && (
                            <>
                                <Divider sx={{ my: 3 }} />
                                <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                                    Residence & boarding
                                </Typography>
                                <Grid container spacing={2}>
                                    {canPickResidence ? (
                                        <Grid item xs={12} sm={6}>
                                            <FormControl fullWidth required>
                                                <InputLabel id="residence-type-label">Residence type</InputLabel>
                                                <Select
                                                    labelId="residence-type-label"
                                                    label="Residence type"
                                                    value={residenceType}
                                                    onChange={(e) => {
                                                        setResidenceType(e.target.value);
                                                        if (e.target.value !== "Boarding") {
                                                            setHostelId("");
                                                            setHostelRoomId("");
                                                        }
                                                    }}
                                                >
                                                    <MenuItem value="Day">Day scholar</MenuItem>
                                                    <MenuItem value="Boarding">Boarder</MenuItem>
                                                </Select>
                                            </FormControl>
                                        </Grid>
                                    ) : (
                                        <Grid item xs={12}>
                                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                                                This school is <strong>boarding only</strong> — all students are registered as boarders.
                                            </Alert>
                                        </Grid>
                                    )}
                                    {residenceType === "Boarding" && (
                                        <HostelRoomPickerFields
                                            hostels={hostels}
                                            studentGender={gender}
                                            hostelId={hostelId || findHostelIdForRoom(hostels, hostelRoomId)}
                                            hostelRoomId={hostelRoomId}
                                            bedLabel={bedLabel}
                                            boardingMode={boardingMode}
                                            roomRequired={boardingMode === "BoardingOnly"}
                                            onHostelChange={(id) => {
                                                setHostelId(id);
                                                setHostelRoomId("");
                                            }}
                                            onRoomChange={(roomId) => {
                                                setHostelRoomId(roomId);
                                                if (roomId) setHostelId(findHostelIdForRoom(hostels, roomId));
                                            }}
                                            onBedLabelChange={setBedLabel}
                                        />
                                    )}
                                </Grid>
                            </>
                        )}

                        <Divider sx={{ my: 3 }} />

                        <Typography variant="subtitle2" fontWeight={700} color="text.secondary" gutterBottom>
                            Admission & fees
                        </Typography>
                        <Grid container spacing={2}>
                            <Grid item xs={12} sm={6}>
                                <TextField
                                    fullWidth
                                    label="Admission Date"
                                    type="date"
                                    value={admissionDate}
                                    onChange={(e) => setAdmissionDate(e.target.value)}
                                    InputLabelProps={{ shrink: true }}
                                />
                            </Grid>
                            <Grid item xs={12} sm={6}>
                                <FormControl fullWidth>
                                    <InputLabel id="admission-status-label">Admission Status</InputLabel>
                                    <Select
                                        labelId="admission-status-label"
                                        label="Admission Status"
                                        value={admissionStatus}
                                        onChange={(e) => setAdmissionStatus(e.target.value)}
                                    >
                                        {ADMISSION_STATUSES.map((item) => (
                                            <MenuItem key={item} value={item}>
                                                {item}
                                            </MenuItem>
                                        ))}
                                    </Select>
                                </FormControl>
                            </Grid>
                            <Grid item xs={12}>
                                <Alert severity="info" sx={{ borderRadius: 2 }}>
                                    Fees are assigned automatically from the active <strong>Fee structure</strong> for
                                    the selected class (same term and academic year). Set up structures under Finance →
                                    Fee structures before enrolling students.
                                </Alert>
                            </Grid>
                        </Grid>

                        <Stack
                            direction={{ xs: "column", sm: "row" }}
                            spacing={1.5}
                            sx={{ mt: 3, justifyContent: "flex-end" }}
                        >
                            <Button variant="outlined" color="inherit" onClick={() => navigate(-1)} sx={{ borderRadius: 2 }}>
                                Cancel
                            </Button>
                            <Button
                                type="submit"
                                variant="contained"
                                size="large"
                                disabled={loader || studentLimitReached}
                                sx={{ borderRadius: 2, minWidth: 160, fontWeight: 700 }}
                            >
                                {loader ? <CircularProgress size={26} color="inherit" /> : "Create student"}
                            </Button>
                        </Stack>
                    </Box>
                </Paper>
            </Container>
            <Popup
                message={message}
                setShowPopup={setShowPopup}
                showPopup={showPopup}
                severity={popupSeverity}
            />
        </Box>
    );
};

export default AddStudent;
